import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

import FormContainer from "../../components/CommonComponents/FormContainer";
import LeftWrapper from "../../components/CommonComponents/LeftWrapper";
import RightWrapper from "../../components/CommonComponents/RightWrapper";
import Wrapper from "../../components/CommonComponents/Wrapper";
import AdminSaveHeader from "../../components/CommonComponents/AdminSaveHeader";
import AdminBackNavigation from "../../components/CommonComponents/AdminBackNavigation";
import MessageLabel from "../../components/CommonComponents/MessageLabel";
import AdminCustomInput from "../../components/CommonComponents/AdminCustomInput";
import AdminCustomSelect from "../../components/CommonComponents/AdminCustomSelect";
import AdminSwitch from "../../components/CommonComponents/AdminSwitch";
import Defaultloadinggif from "../../layout/Others/Defaultloadinggif";
import Response_Processing from "../../layout/Others/Response_Processing";
import MessageBox from "../../components/CommonComponents/MessageBox";

import { getItemToken } from "../../Utils/localStorageUtils";

const DeviceCropRuleAddUpdate = () => {
  const baseURL = process.env.REACT_APP_Baseurl;
  const headers = getItemToken("access_token");

  const createApi = `${baseURL}/admin/device-crops/index`;
  const editApi = `${baseURL}/admin/device-crops/edit`;
  const storeApi = `${baseURL}/admin/device-crops/store`;
  const updateApi = `${baseURL}/admin/device-crops/update`;

  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const [showOverlay, setShowOverlay] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    device: "",
    code: "",
    orientation_id: "",
    width: "",
    height: "",
    active: 1,
  });
  const [formDataError, setFormDataError] = useState({});

  const [orientationOptions, setOrientationOptions] = useState([]);

  const combinedRef = useRef({ device: null, code: null, orientation_id: null, width: null, height: null, active: null });

  const focusInput = (inputName) => {
    if (combinedRef.current[inputName]) {
      combinedRef.current[inputName].focus();
    }
  };

  const deviceOptions = [
    { value: "web", name: "Web" },
    { value: "ios", name: "iOS" },
    { value: "android", name: "Android" },
    { value: "tv", name: "TV" },
  ];

  const fetchOrientations = async () => {
    try {
      const resp = await axios.get(`${baseURL}/admin/orientation/index`, { headers });
      const list = resp?.data?.data || [];
      setOrientationOptions(list.map(o => ({ value: o.id, name: `${o.slug || o.name}${o.images_size ? ` (${o.images_size})` : ""}` }))); 
    } catch (err) {
      // ignore for now
    }
  };

  // Fetch for edit
  const fetchEditData = async () => {
    if (!id) return;
    setShowOverlay(true);
    try {
      const response = await axios.get(`${editApi}/${id}`, { headers });
      const result = response?.data;
      if (result?.status && result?.data) {
        setFormData({
          device: result.data.device || "",
          code: result.data.code || "",
          orientation_id: result.data.orientation_id || "",
          width: String(result.data.width || ""),
          height: String(result.data.height || ""),
          active: result.data.active ?? 1,
        });
      } else {
        toast.error(result?.message || "Failed to load crop rule.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Error fetching crop rule.");
    } finally {
      setShowOverlay(false);
    }
  };

  // Init
  useEffect(() => {
    fetchOrientations();
    if (id) fetchEditData();
  }, [id, location]);

  const handleInput = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked ? 1 : 0 }));
      setFormDataError((prev) => ({ ...prev, [name]: "" }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormDataError((prev) => ({ ...prev, [name]: "" }));
  };

  const autoGenerateCodeIfEmpty = () => {
    if (!formData?.code) {
      const orientationLabel = orientationOptions.find(o => String(o.value) === String(formData.orientation_id))?.name || "";
      const orientationSlug = orientationLabel.split(" ")[0] || "orientation";
      const sizePart = formData.width && formData.height ? `${formData.width}x${formData.height}` : "";
      const code = `${(formData.device || "device").toLowerCase()}_${orientationSlug.toLowerCase()}${sizePart ? `_${sizePart}` : ""}`;
      setFormData((prev) => ({ ...prev, code }));
    }
  };

  const validate = () => {
    let valid = true;
    if (!formData?.device) {
      setFormDataError((prev) => ({ ...prev, device: "Device is required." }));
      focusInput("device");
      valid = false;
    }
    if (!formData?.orientation_id) {
      setFormDataError((prev) => ({ ...prev, orientation_id: "Orientation is required." }));
      if (valid) focusInput("orientation_id");
      valid = false;
    }
    if (!formData?.width || Number(formData.width) <= 0) {
      setFormDataError((prev) => ({ ...prev, width: "Width must be a positive number." }));
      if (valid) focusInput("width");
      valid = false;
    }
    if (!formData?.height || Number(formData.height) <= 0) {
      setFormDataError((prev) => ({ ...prev, height: "Height must be a positive number." }));
      if (valid) focusInput("height");
      valid = false;
    }
    if (!formData?.code || formData?.code?.trim().length < 2) {
      setFormDataError((prev) => ({ ...prev, code: "Code is required." }));
      if (valid) focusInput("code");
      valid = false;
    }
    return valid;
  };

  const handleStore = async (e) => {
    e.preventDefault();
    if (!formData?.code) autoGenerateCodeIfEmpty();
    setTimeout(async () => {
      if (!validate()) return;
      setLoading(true);
      try {
        const payload = {
          device: formData.device,
          code: formData.code,
          orientation_id: Number(formData.orientation_id),
          width: Number(formData.width),
          height: Number(formData.height),
          active: formData.active,
        };
        if (!id) {
          const response = await axios.post(`${storeApi}`, payload, { headers });
          const result = response?.data;
          if (result?.status) {
            toast.success(result?.message || "Crop rule created successfully");
            navigate("/device-crop-rules");
          } else {
            toast.error(result?.message || "Failed to create crop rule");
          }
        } else {
          const response = await axios.patch(`${updateApi}/${id}`, payload, { headers });
          const result = response?.data;
          if (result?.status) {
            toast.success(result?.message || "Crop rule updated successfully");
          } else {
            toast.error(result?.message || "Failed to update crop rule");
          }
        }
      } catch (err) {
        toast.error(err?.response?.data?.message || "Request failed");
      } finally {
        setLoading(false);
      }
    }, 0);
  };

  if (showOverlay) {
    return <Defaultloadinggif className="heightOnLoader" />;
  }

  return (
    <React.Fragment>
      {showOverlay && <Response_Processing />}
      <AdminBackNavigation path="/device-crop-rules" text="Back" />
      <AdminSaveHeader
        heading={!id ? "Add Crop Rule" : "Update Crop Rule"}
        saveText={!id ? "Save" : "Update"}
        saveMethod={handleStore}
        id={"device-crop-rule-save-button"}
      />
      <FormContainer className="pb-5">
        <LeftWrapper>
          <Wrapper>
            <MessageLabel text="Device" noMargin className="mandatorySimple" />
            <AdminCustomSelect
              id="device-type"
              name="device"
              value={formData?.device}
              onChange={handleInput}
              options={deviceOptions}
              defaultOptionName="Select device"
              ref={(el) => (combinedRef.current.device = el)}
              error={formDataError?.device}
            />

            <MessageLabel text="Code" noMargin className="mandatorySimple" />
            <AdminCustomInput
              id="crop-code"
              type="text"
              name="code"
              value={formData?.code}
              onChange={handleInput}
              placeHolder="Unique code (e.g., web_portrait_1080x1920)"
              ref={(el) => (combinedRef.current.code = el)}
              error={formDataError?.code}
            />

            <MessageLabel text="Orientation" noMargin className="mandatorySimple" />
            <AdminCustomSelect
              id="orientation-id"
              name="orientation_id"
              value={formData?.orientation_id}
              onChange={handleInput}
              options={orientationOptions}
              defaultOptionName="Select orientation"
              ref={(el) => (combinedRef.current.orientation_id = el)}
              error={formDataError?.orientation_id}
            />
          </Wrapper>
        </LeftWrapper>
        <RightWrapper>
          <Wrapper>
            <div className="row">
              <div className="col-6">
                <MessageLabel text="Width" noMargin className="mandatorySimple" />
                <AdminCustomInput
                  id="crop-width"
                  type="number"
                  name="width"
                  value={formData?.width}
                  onChange={handleInput}
                  placeHolder="Width"
                  ref={(el) => (combinedRef.current.width = el)}
                  error={formDataError?.width}
                />
              </div>
              <div className="col-6">
                <MessageLabel text="Height" noMargin className="mandatorySimple" />
                <AdminCustomInput
                  id="crop-height"
                  type="number"
                  name="height"
                  value={formData?.height}
                  onChange={handleInput}
                  placeHolder="Height"
                  ref={(el) => (combinedRef.current.height = el)}
                  error={formDataError?.height}
                />
              </div>
            </div>

            <div className="d-flex justify-content-between pt-2 mb-2 ">
              <div className="col-6 col-md-6 pe-2">
                <MessageBox text="Active" />
              </div>
              <div className="col-6 col-md-6 ps-2 text-end">
                <AdminSwitch id="crop-active" name={'active'} checked={formData?.active == 1}
                  onChange={(e) =>
                    handleInput({
                      target: {
                        name: "active",
                        type: "checkbox",
                        checked: e.target.checked,
                      },
                    })
                  }
                  ref={(el) => (combinedRef.current.active = el)}
                />
              </div>
            </div>
          </Wrapper>
        </RightWrapper>
      </FormContainer>
    </React.Fragment>
  );
};

export default DeviceCropRuleAddUpdate;

